import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AUTH_ROUTE_PATH } from '@app/auth/models/auth-user.model';
import { AuthStateService } from '@app/auth/services/auth-state.service';
import { SessionControllerService } from '@app/auth/services/session-controller.service';
import { BEARER_TOKEN_PREFIX, HTTP_HEADER } from '@shared/models/http-header.model';

const SESSION_ENDPOINT_SUFFIX = '/api/v1/me';
const SCOPE_ENDPOINT_SUFFIX = '/api/v1/me/scope';
const DRIVERS_ENDPOINT_SUFFIX = '/api/v1/drivers';

export const handlerErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const session = inject(SessionControllerService);
  const auth = inject(AuthStateService);
  const isSessionRequest =
    req.url.endsWith(SESSION_ENDPOINT_SUFFIX) || req.url.endsWith(SCOPE_ENDPOINT_SUFFIX);
  const isDriversRequest = req.url.endsWith(DRIVERS_ENDPOINT_SUFFIX);

  const applyErrorPolicy = (err: HttpErrorResponse): Observable<never> => {
    if (err.status === HttpStatusCode.Unauthorized) session.signOut();
    if (err.status === HttpStatusCode.Forbidden && !isDriversRequest) {
      router.navigate([AUTH_ROUTE_PATH.NO_ACCESS]);
    }
    return throwError(() => err);
  };

  const retryWithFreshToken = (err: HttpErrorResponse): Observable<HttpEvent<unknown>> =>
    auth.getAccessToken({ forceRefresh: true }).pipe(
      catchError(() => throwError(() => err)),
      switchMap((token) => next(withBearerToken(req, token))),
      catchError((retryErr: HttpErrorResponse) => applyErrorPolicy(retryErr)),
    );

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (isSessionRequest) return throwError(() => err);
      if (isPossiblyStaleTokenRejection(err, req, isDriversRequest)) {
        return retryWithFreshToken(err);
      }
      return applyErrorPolicy(err);
    }),
  );
};

function isPossiblyStaleTokenRejection(
  err: HttpErrorResponse,
  req: HttpRequest<unknown>,
  isDriversRequest: boolean,
): boolean {
  return (
    err.status === HttpStatusCode.Forbidden &&
    !isDriversRequest &&
    req.headers.has(HTTP_HEADER.AUTHORIZATION)
  );
}

function withBearerToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { [HTTP_HEADER.AUTHORIZATION]: `${BEARER_TOKEN_PREFIX} ${token}` },
  });
}
